import React, { useState } from 'react';
import { Clock, ChevronDown, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { StateInstance } from '../../types/mold';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import { calculateDuration, formatShortDateTime } from '@/utils/dateUtils';

interface HorizontalStepDetailCardProps {
    timestamp: string;
    endTime: string | null;
    assignedTo: string;
    assigneeData: any;
    stateInstance: StateInstance | null | undefined;
    isCurrentStep: boolean;
    onClick: () => void;
}

const HorizontalStepDetailCard: React.FC<HorizontalStepDetailCardProps> = ({
    timestamp,
    assignedTo,
    endTime,
    assigneeData,
    stateInstance,
    isCurrentStep,
    onClick,
}) => {
    const [showApprovalDetails, setShowApprovalDetails] = useState(false);
    const getApprovalStatusIcon = (status: string) => {
        switch (status) {
            case 'approved':
                return { icon: CheckCircle, color: 'text-green-600 bg-green-500', text: 'อนุมัติ' };
            case 'rejected':
                return { icon: XCircle, color: 'text-red-600 bg-red-500', text: 'ปฏิเสธ' };
            case 'pending':
            default:
                return { icon: AlertCircle, color: 'text-orange-600 bg-orange-500', text: 'รอผล' };
        }
    };

    // Get approval summary
    const getApprovalSummary = () => {
        if (!stateInstance?.approverSlots) return null;

        const approved = stateInstance.approverSlots.filter((slot: any) => slot.status === 'approved').length;
        const rejected = stateInstance.approverSlots.filter((slot: any) => slot.status === 'rejected').length;
        const pending = stateInstance.approverSlots.filter((slot: any) => slot.status === 'pending').length;
        const total = stateInstance.approverSlots.length;

        return { approved, rejected, pending, total };
    };

    const approvalSummary = getApprovalSummary();

    return (
        <div
            onClick={() => onClick()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    onClick();
                }
            }}
            className="cursor-pointer flex flex-col bg-white rounded-lg w-full shadow-md border border-gray-200 overflow-hidden max-w-xs sm:max-w-sm
        hover:shadow-lg hover:-translate-y-0.5 transform transition-all duration-150 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-1"
        >
            {/* Header with assignee and timestamp */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 border-b border-gray-100">
                <div className="w-full flex items-center justify-center gap-2 mb-1">
                    <UserProfileImage currentAssignee={assigneeData} size="xs" />
                    <div>
                        <p className="text-xs font-semibold text-gray-800 truncate">{assignedTo}</p>
                        <p className="text-xs text-gray-600">{formatShortDateTime(timestamp)}</p>
                    </div>
                </div>
            </div>


            <div className="text-center flex-shrink-0 py-2">
                {endTime && (
                    <div className="inline-flex items-center sm:gap-1 text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded-full">
                        <Clock className="w-3 h-3" />
                        <span className="hidden lg:inline">เวลาที่ใช้</span>
                        <span className="inline">{calculateDuration(timestamp, endTime) ?? '-'}</span>
                    </div>
                )}
                {isCurrentStep && (
                    <div className="inline-flex items-center text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded-full">
                        <Clock className="w-3 h-3 mr-1" />
                        <span className="inline">กำลังดำเนินการ</span>
                    </div>
                )}
            </div>

            {/* Approvals Section */}
            {stateInstance?.approverSlots && stateInstance.approverSlots.length > 0 && (
                <div className="border-b border-gray-100">
                    {/* Approval Summary - Always Visible */}
                    <div
                        className="p-1 md:p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => setShowApprovalDetails(!showApprovalDetails)}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3 text-gray-600 flex-shrink-0" />
                                <span className="text-xs font-semibold text-gray-700">
                                    อนุมัติ({approvalSummary?.total || 0})
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                {/* Summary indicators */}
                                <div className="flex items-center gap-1">
                                    {approvalSummary && approvalSummary.approved > 0 && (
                                        <div className="flex items-center text-xs">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-xs text-green-700 font-medium">{approvalSummary.approved}</span>
                                        </div>
                                    )}
                                    {approvalSummary && approvalSummary.rejected > 0 && (
                                        <div className="flex items-center text-xs">
                                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                            <span className="text-xs text-red-700 font-medium">{approvalSummary.rejected}</span>
                                        </div>
                                    )}
                                    {approvalSummary && approvalSummary.pending > 0 && (
                                        <div className="flex items-center text-xs">
                                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                                            <span className="text-xs text-orange-700 font-medium">{approvalSummary.pending}</span>
                                        </div>
                                    )}
                                </div>
                                <ChevronDown
                                    className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${showApprovalDetails ? 'transform rotate-180' : ''
                                        }`}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Detailed Approval List - Expandable */}
                    <div className={`overflow-hidden transition-all duration-300 ${showApprovalDetails ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}>
                        <div className="p-3 bg-white space-y-2">
                            {stateInstance.approverSlots.map((slot: any, index: number) => {
                                const statusInfo = getApprovalStatusIcon(slot.status);
                                // derive a bg color class for the small dot from the returned color string
                                const dotClassMatch = (statusInfo.color || '').match(/bg-[^\s]+/);
                                const dotClass = dotClassMatch ? dotClassMatch[0] : 'bg-gray-300';
                                return (
                                    <div key={slot.id || index} className="flex items-center gap-3 text-xs py-1">
                                        {/* small colored dot */}
                                        <span className={`${dotClass} w-2 h-2 rounded-full flex-shrink-0`} aria-hidden />

                                        {/* approver name (compact) */}
                                        <span className="text-gray-700 truncate text-sm">{slot.actualApproverUser?.fullname || slot.actualApproverUser?.username || `แผนก ${slot.assignedRole?.role}` || 'ไม่ระบุ'}</span>

                                        {/* compact status label on the right */}
                                        <span className="ml-auto text-gray-500 text-xs font-medium">{statusInfo.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Notes Section - Compact Professional Style */}
            {stateInstance?.notes && (
                <div className="mx-2 mb-2">
                    <div className="bg-white border border-gray-200 rounded-md shadow-sm">
                        {/* Compact Comment Body */}
                        <div className="px-2 py-1">
                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap line-clamp-2">
                                {stateInstance?.notes}
                            </p>
                            <time className="text-xs text-gray-400 flex-shrink-0 ml-1">
                                {formatShortDateTime(stateInstance?.startTime || stateInstance?.updatedAt || '')}
                            </time>
                        </div>
                        {/* Compact Comment Header */}
                        <div className="flex items-center gap-2 px-2 py-1 bg-gray-50 border-b border-gray-100 rounded-t-md">
                            <UserProfileImage
                                currentAssignee={stateInstance?.assignedTo}
                                size="xs"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <p className="text-xs font-medium text-gray-900 truncate">
                                        {stateInstance?.assignedTo?.fullname ?? stateInstance?.assignedTo?.username ?? 'ไม่ระบุ'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default HorizontalStepDetailCard;